import type { ReactNode } from 'react'
import { useAdminOverview } from '../overview/admin-overview-provider'
import { DonutWheel, SegmentBar } from '../shared/charts'
import type { KindCounts, TypeCounts } from '../store'

const KIND_ROWS: { key: keyof KindCounts; label: string; color: string }[] = [
  { key: 'praise', label: 'Praise', color: 'var(--cd-green)' },
  { key: 'critique', label: 'Critique', color: 'var(--cd-red)' },
  { key: 'suggestion', label: 'Suggestion', color: 'var(--cd-purple)' },
]

const TYPE_ROWS: { key: keyof TypeCounts; label: string; color: string }[] = [
  { key: 'coding', label: 'Coding', color: 'var(--cd-amber)' },
  { key: 'sd', label: 'Sys design', color: 'var(--cd-purple)' },
  { key: 'behavioral', label: 'Behavioral', color: 'var(--cd-blue)' },
  { key: 'task', label: 'Task', color: 'var(--cd-gray)' },
]

const EMPTY_KIND: KindCounts = { praise: 0, critique: 0, suggestion: 0 }
const EMPTY_TYPE: TypeCounts = { coding: 0, sd: 0, behavioral: 0, task: 0 }

function kindSegments(counts: KindCounts) {
  return KIND_ROWS.map((row) => ({
    label: row.label,
    color: row.color,
    count: counts[row.key],
  }))
}

function typeSegments(counts: TypeCounts) {
  return TYPE_ROWS.map((row) => ({
    label: row.label,
    color: row.color,
    count: counts[row.key],
  }))
}

/**
 * Overall homework-completion percent (spec §6.3 READINESS): sum of completed
 * over sum of scheduled across every track/week cohort. Returns `null` when
 * nothing is scheduled so the cell renders an honest em dash rather than 0 / a
 * fabricated score — a null never enters the ratio.
 */
function overallCompletionPercent(
  cohorts: { scheduled: number; completed: number }[],
): number | null {
  let scheduled = 0
  let completed = 0
  for (const cohort of cohorts) {
    scheduled += cohort.scheduled
    completed += cohort.completed
  }
  if (scheduled === 0) return null
  return Math.round((completed / scheduled) * 100)
}

/**
 * Stat strip (spec §6.3): one card, three cells split by 1px dividers. All
 * three cells are live off the engagement overview — READINESS is derived
 * completion, FEEDBACK MIX and BY QUESTION TYPE are the this/last-week windows.
 */
export function StatStrip(): ReactNode {
  const { overview } = useAdminOverview()

  const readiness = overallCompletionPercent(overview?.completionByTrack ?? [])
  const feedbackMix = overview?.feedbackMix ?? {
    thisWeek: EMPTY_KIND,
    lastWeek: EMPTY_KIND,
  }
  const byQuestionType = overview?.byQuestionType ?? {
    thisWeek: EMPTY_TYPE,
    lastWeek: EMPTY_TYPE,
  }

  return (
    <div className="grid grid-cols-1 divide-y divide-(--cd-border) rounded-xl border border-(--cd-border) bg-(--cd-card) md:grid-cols-3 md:divide-x md:divide-y-0">
      {/* READINESS */}
      <div className="flex flex-col gap-1.5 p-4">
        <span className="text-[11px] font-semibold tracking-wide text-(--cd-text-faint) uppercase">
          Readiness
        </span>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-(--cd-green)">
            {readiness ?? '—'}
          </span>
        </div>
        <p className="text-xs text-(--cd-text-muted)">
          Overall homework completion across tracks
        </p>
      </div>

      {/* FEEDBACK MIX */}
      <div className="flex flex-col gap-1.5 p-4">
        <span className="text-[11px] font-semibold tracking-wide text-(--cd-text-faint) uppercase">
          Feedback mix
        </span>
        <div className="flex items-center gap-4">
          <DonutWheel
            segments={kindSegments(feedbackMix.thisWeek)}
            caption="This wk"
          />
          <DonutWheel
            segments={kindSegments(feedbackMix.lastWeek)}
            caption="Last wk"
          />
          <div className="flex flex-col gap-1">
            {KIND_ROWS.map((row) => (
              <div
                key={row.key}
                className="flex items-center gap-1.5 text-[11px] text-(--cd-text-2)"
              >
                <span
                  className="size-1.5 rounded-full"
                  style={{ background: row.color }}
                />
                <span>{row.label}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-[10px] text-(--cd-text-faint)">
          center = total comments
        </p>
      </div>

      {/* BY QUESTION TYPE */}
      <div className="flex flex-col gap-1.5 p-4">
        <span className="text-[11px] font-semibold tracking-wide text-(--cd-text-faint) uppercase">
          By question type
        </span>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="w-10 text-[10px] text-(--cd-text-faint)">
              This wk
            </span>
            <SegmentBar segments={typeSegments(byQuestionType.thisWeek)} />
          </div>
          <div className="flex items-center gap-2">
            <span className="w-10 text-[10px] text-(--cd-text-faint)">
              Last wk
            </span>
            <SegmentBar segments={typeSegments(byQuestionType.lastWeek)} />
          </div>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {TYPE_ROWS.map((row) => (
            <div
              key={row.key}
              className="flex items-center gap-1.5 text-[11px] text-(--cd-text-2)"
            >
              <span
                className="size-1.5 rounded-full"
                style={{ background: row.color }}
              />
              <span>{row.label}</span>
              <span className="font-mono text-(--cd-text-faint)">
                {byQuestionType.thisWeek[row.key]}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
